import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import styles from '../../styles/modules/admin/AdminReports.module.css';
import headerStyles from '../../styles/modules/Header.module.css';

interface OverviewData {
  usersByType: {
    staff: number;
    employer: number;
  };
  usersByRole: Record<string, number>;
  jobOffersByStatus: {
    open: number;
    filled: number;
    cancelled: number;
  };
  availabilityByStatus: {
    available: number;
    booked: number;
    cancelled: number;
  };
  recentActivity: {
    jobOffersLast30Days: number;
    availabilityLast30Days: number;
  };
}

interface UsersByRoleData {
  totalUsers: number;
  breakdown: Array<{
    role: string;
    count: number;
    percentage: number;
  }>;
}

interface ActivityTrendsData {
  period: number;
  newUsersPerDay: number;
  newJobRequestsPerDay: number;
  newAvailabilityPerDay: number;
  totalNewUsers: number;
  totalNewJobRequests: number;
  totalNewAvailability: number;
}

interface StaffMember {
  userId: string;
  username: string;
  fullName: string;
  role: string;
  availabilityCount?: number;
  bookedCount?: number;
  jobsCompleted?: number;
}

interface StaffPerformanceData {
  topStaffByAvailability: StaffMember[];
  topStaffByJobs: StaffMember[];
}

const AdminReports: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [usersByRole, setUsersByRole] = useState<UsersByRoleData | null>(null);
  const [activityTrends, setActivityTrends] = useState<ActivityTrendsData | null>(null);
  const [staffPerformance, setStaffPerformance] = useState<StaffPerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [trendsDays, setTrendsDays] = useState(30);

  useEffect(() => {
    fetchAllReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendsDays]);

  const fetchAllReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
      };

      // Fetch all reports in parallel
      const [overviewRes, usersByRoleRes, activityRes, staffPerfRes] = await Promise.all([
        fetch(getApiUrl('api/admin/reports/overview'), { headers }),
        fetch(getApiUrl('api/admin/reports/users-by-role'), { headers }),
        fetch(getApiUrl(`api/admin/reports/activity-trends?days=${trendsDays}`), { headers }),
        fetch(getApiUrl('api/admin/reports/staff-performance?limit=10'), { headers }),
      ]);

      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setOverview(data);
      }

      if (usersByRoleRes.ok) {
        const data = await usersByRoleRes.json();
        setUsersByRole(data);
      }

      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivityTrends(data);
      }

      if (staffPerfRes.ok) {
        const data = await staffPerfRes.json();
        setStaffPerformance(data);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    try {
      if (!overview) return;

      // Create CSV content
      let csvContent = "data:text/csv;charset=utf-8,";
      
      // Add header
      csvContent += "Easy Staff - Admin Reports\n";
      csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;

      // Users Overview
      csvContent += "USERS OVERVIEW\n";
      csvContent += "Type,Count\n";
      csvContent += `Staff,${overview.usersByType.staff}\n`;
      csvContent += `Employers,${overview.usersByType.employer}\n`;
      csvContent += `Total,${overview.usersByType.staff + overview.usersByType.employer}\n\n`;

      // Users by Role
      if (usersByRole) {
        csvContent += "USERS BY ROLE\n";
        csvContent += "Role,Count,Percentage\n";
        usersByRole.breakdown.forEach(item => {
          csvContent += `${item.role},${item.count},${item.percentage.toFixed(1)}%\n`;
        });
        csvContent += "\n";
      }

      // Job Offers Stats
      csvContent += "JOB OFFERS STATISTICS\n";
      csvContent += "Status,Count\n";
      csvContent += `Open,${overview.jobOffersByStatus.open}\n`;
      csvContent += `Filled,${overview.jobOffersByStatus.filled}\n`;
      csvContent += `Cancelled,${overview.jobOffersByStatus.cancelled}\n\n`;

      // Availability Stats
      csvContent += "AVAILABILITY STATISTICS\n";
      csvContent += "Status,Count\n";
      csvContent += `Available,${overview.availabilityByStatus.available}\n`;
      csvContent += `Booked,${overview.availabilityByStatus.booked}\n`;
      csvContent += `Cancelled,${overview.availabilityByStatus.cancelled}\n\n`;

      // Activity Trends
      if (activityTrends) {
        csvContent += `ACTIVITY TRENDS (${activityTrends.period} days)\n`;
        csvContent += "Metric,Total,Per Day\n";
        csvContent += `New Users,${activityTrends.totalNewUsers},${activityTrends.newUsersPerDay.toFixed(1)}\n`;
        csvContent += `New Job Requests,${activityTrends.totalNewJobRequests},${activityTrends.newJobRequestsPerDay.toFixed(1)}\n`;
        csvContent += `New Availability,${activityTrends.totalNewAvailability},${activityTrends.newAvailabilityPerDay.toFixed(1)}\n\n`;
      }

      // Staff Performance
      if (staffPerformance) {
        csvContent += "TOP STAFF BY AVAILABILITY\n";
        csvContent += "Rank,Name,Role,Posted,Booked\n";
        staffPerformance.topStaffByAvailability.forEach((staff, index) => {
          csvContent += `${index + 1},${staff.fullName},${staff.role},${staff.availabilityCount},${staff.bookedCount}\n`;
        });
        csvContent += "\n";

        csvContent += "TOP STAFF BY JOBS COMPLETED\n";
        csvContent += "Rank,Name,Role,Completed\n";
        staffPerformance.topStaffByJobs.forEach((staff, index) => {
          csvContent += `${index + 1},${staff.fullName},${staff.role},${staff.jobsCompleted}\n`;
        });
      }

      // Create download link
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `admin-reports-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting data:', error);
      showToast({ message: 'Error exporting data. Please try again.', type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className={styles.adminReports}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className={styles.adminReports}>
        <div className={styles.error}>{t('admin.reports.errorLoading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.adminReports}>
      <div className={headerStyles.listPageHeader}>
        <button onClick={() => navigate('/admin')} className={headerStyles.backButton}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1 className={headerStyles.titleList}>{t('admin.reports.title')}</h1>
        <button className={styles.exportBtn} onClick={handleExport}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <span>{t('admin.reports.export')}</span>
        </button>
      </div>

      <div className={styles.pageContent}>
        {/* Activity Trends Section */}
        {activityTrends && (
          <section className={styles.reportSection}>
            <div className={styles.sectionHeader}>
              <h2>{t('admin.reports.activityTrends')}</h2>
              <div className={styles.trendPeriodSelector}>
                <button 
                  className={trendsDays === 7 ? 'active' : ''}
                  onClick={() => setTrendsDays(7)}
                >
                  7 {t('admin.reports.days')}
                </button>
                <button 
                  className={trendsDays === 30 ? 'active' : ''}
                  onClick={() => setTrendsDays(30)}
                >
                  30 {t('admin.reports.days')}
                </button>
                <button 
                  className={trendsDays === 90 ? 'active' : ''}
                  onClick={() => setTrendsDays(90)}
                >
                  90 {t('admin.reports.days')}
                </button>
              </div>
            </div>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div className={styles.statInfo}>
                  <h3>{activityTrends.totalNewUsers}</h3>
                  <p>{t('admin.reports.newUsers')}</p>
                  <span className={styles.statDetail}>{activityTrends.newUsersPerDay.toFixed(1)} {t('admin.reports.perDay')}</span>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                </div>
                <div className={styles.statInfo}>
                  <h3>{activityTrends.totalNewJobRequests}</h3>
                  <p>{t('admin.reports.newJobRequests')}</p>
                  <span className={styles.statDetail}>{activityTrends.newJobRequestsPerDay.toFixed(1)} {t('admin.reports.perDay')}</span>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statIcon}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div className={styles.statInfo}>
                  <h3>{activityTrends.totalNewAvailability}</h3>
                  <p>{t('admin.reports.newAvailability')}</p>
                  <span className={styles.statDetail}>{activityTrends.newAvailabilityPerDay.toFixed(1)} {t('admin.reports.perDay')}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Users Overview */}
        <section className={styles.reportSection}>
          <h2>{t('admin.reports.usersOverview')}</h2>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.usersByType.staff}</h3>
                <p>{t('admin.reports.staffMembers')}</p>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18"/>
                  <path d="M9 8h1"/>
                  <path d="M9 12h1"/>
                  <path d="M9 16h1"/>
                  <path d="M14 8h1"/>
                  <path d="M14 12h1"/>
                  <path d="M14 16h1"/>
                  <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.usersByType.employer}</h3>
                <p>{t('admin.reports.employers')}</p>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="20" x2="12" y2="10"/>
                  <line x1="18" y1="20" x2="18" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="16"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.usersByType.staff + overview.usersByType.employer}</h3>
                <p>{t('admin.reports.totalUsers')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Users by Role */}
        {usersByRole && (
          <section className={styles.reportSection}>
            <h2>{t('admin.reports.usersByRole')}</h2>
            <div className={styles.roleBreakdown}>
              {usersByRole.breakdown.map((item, index) => (
                <div key={index} className={styles.roleItem}>
                  <div className={styles.roleInfo}>
                    <span className={styles.roleName}>{item.role}</span>
                    <span className={styles.rolePercentage}>{item.percentage.toFixed(1)}%</span>
                  </div>
                  <div className={styles.roleBar}>
                    <div 
                      className={styles.roleBarFill} 
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                  <span className={styles.roleCount}>{item.count} {t('admin.reports.users')}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Job Offers Statistics */}
        <section className={styles.reportSection}>
          <h2>{t('admin.reports.jobOffersStats')}</h2>
          <div className={styles.statsGrid}>
            <div className={`${styles.statCard} ${styles.success}`}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.jobOffersByStatus.open}</h3>
                <p>{t('admin.reports.openOffers')}</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.info}`}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.jobOffersByStatus.filled}</h3>
                <p>{t('admin.reports.filledOffers')}</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.warning}`}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.jobOffersByStatus.cancelled}</h3>
                <p>{t('admin.reports.cancelledOffers')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Availability Statistics */}
        <section className={styles.reportSection}>
          <h2>{t('admin.reports.availabilityStats')}</h2>
          <div className={styles.statsGrid}>
            <div className={`${styles.statCard} ${styles.success}`}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.availabilityByStatus.available}</h3>
                <p>{t('admin.reports.available')}</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.info}`}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.availabilityByStatus.booked}</h3>
                <p>{t('admin.reports.booked')}</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.warning}`}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.availabilityByStatus.cancelled}</h3>
                <p>{t('admin.reports.cancelledAvailability')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Recent Activity */}
        <section className={styles.reportSection}>
          <h2>{t('admin.reports.recentActivity')}</h2>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.recentActivity.jobOffersLast30Days}</h3>
                <p>{t('admin.reports.jobOffersLast30Days')}</p>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className={styles.statInfo}>
                <h3>{overview.recentActivity.availabilityLast30Days}</h3>
                <p>{t('admin.reports.availabilityLast30Days')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Staff Performance */}
        {staffPerformance && (
          <section className={styles.reportSection}>
            <h2>{t('admin.reports.staffPerformance')}</h2>
            <div className={styles.performanceGrid}>
              <div className={styles.performanceSection}>
                <h3>{t('admin.reports.topByAvailability')}</h3>
                <div className={styles.staffList}>
                  {staffPerformance.topStaffByAvailability.map((staff, index) => (
                    <div key={staff.userId} className={styles.staffItem}>
                      <span className={styles.staffRank}>#{index + 1}</span>
                      <div className={styles.staffInfo}>
                        <span className={styles.staffName}>{staff.fullName}</span>
                        <span className={styles.staffRole}>{staff.role}</span>
                      </div>
                      <div className={styles.staffStats}>
                        <span>{staff.availabilityCount} {t('admin.reports.posted')}</span>
                        <span className={styles.statSeparator}>|</span>
                        <span>{staff.bookedCount} {t('admin.reports.booked')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.performanceSection}>
                <h3>{t('admin.reports.topByJobs')}</h3>
                <div className={styles.staffList}>
                  {staffPerformance.topStaffByJobs.map((staff, index) => (
                    <div key={staff.userId} className={styles.staffItem}>
                      <span className={styles.staffRank}>#{index + 1}</span>
                      <div className={styles.staffInfo}>
                        <span className={styles.staffName}>{staff.fullName}</span>
                        <span className={styles.staffRole}>{staff.role}</span>
                      </div>
                      <div className={styles.staffStats}>
                        <span>{staff.jobsCompleted} {t('admin.reports.completed')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>

    </div>
  );
};

export default AdminReports;
